import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { submitChatFeedback } from '../../../services/agentChat.service';
import { safeAnalytics } from '../../../utils/analytics';

export type SessionRatingSurface = 'chat' | 'live';
export type SessionRatingValue = 'up' | 'down';

/**
 * Score a session's trace as `user-feedback`, through the same call and route
 * the per-message thumb uses (`submitChatFeedback` -> agent-service
 * `POST /feedback`), so the weekly agent-quality helper reads it with no change:
 * it rolls every `user-feedback` score up to the session whose trace it is on.
 *
 * Tracked only once the route accepted it, for the reason `MessageFeedback`
 * gives: the route can refuse a rating, and an event fired on click is never
 * retracted. A refused rating is tracked as Failed, so a lost tap never reads
 * as an ignored prompt. Resolves whether the rating was recorded.
 */
export async function submitSessionRating(params: {
  surface: SessionRatingSurface;
  sessionId: string;
  traceId: string;
  value: SessionRatingValue;
}): Promise<boolean> {
  const { surface, sessionId, traceId, value } = params;
  const score = value === 'up' ? 1 : 0;
  const ok = await submitChatFeedback(traceId, score);
  safeAnalytics.track(
    ok ? ANALYTICS_EVENTS.SESSION_RATING_SUBMITTED : ANALYTICS_EVENTS.SESSION_RATING_FAILED,
    { surface, session_id: sessionId, trace_id: traceId, score, value },
  );
  return ok;
}

export function trackSessionRating(
  event:
    | typeof ANALYTICS_EVENTS.SESSION_RATING_SHOWN
    | typeof ANALYTICS_EVENTS.SESSION_RATING_DISMISSED,
  props: { surface: SessionRatingSurface; sessionId: string; traceId: string },
): void {
  safeAnalytics.track(event, {
    surface: props.surface,
    session_id: props.sessionId,
    trace_id: props.traceId,
  });
}
